import React from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { fontStyle } from '../config/fonts';

type IoniconsName = React.ComponentProps<typeof Ionicons>['name'];

interface NavItem {
  icon: IoniconsName;
  label: string;
  route?: string;
  onPress?: () => void;
}

interface LeftSidebarProps {
  onClose?: () => void;
}

export default function LeftSidebar({ onClose }: LeftSidebarProps) {
  const { colors } = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const mainNavItems: NavItem[] = [
    { icon: 'home-outline', label: 'Home', route: '/' },
    { icon: 'flame-outline', label: 'Popular', route: '/' },
    { icon: 'compass-outline', label: 'Explore', route: '/zones' },
    { icon: 'globe-outline', label: 'All', route: '/' },
  ];

  const customFeedsItems: NavItem[] = [
    { icon: 'add-outline', label: 'Create a custom feed', onPress: () => {} },
  ];

  const recentItems: NavItem[] = [
    { icon: 'time-outline', label: 'Recent Zone 1', route: '/' },
    { icon: 'time-outline', label: 'Recent Zone 2', route: '/' },
  ];

  const handleNavPress = (item: NavItem) => {
    if (item.onPress) {
      item.onPress();
    } else if (item.route) {
      router.push(item.route as any);
    }
    onClose?.();
  };

  const renderSection = (title: string, items: NavItem[], showDivider = true) => (
    <View style={styles.section}>
      {title && (
        <Text style={[styles.sectionTitle, fontStyle, { color: colors.textSecondary }]}>
          {title}
        </Text>
      )}
      {items.map((item, index) => (
        <Pressable
          key={`${item.label}-${index}`}
          style={({ pressed }) => [
            styles.navItem,
            pressed && { backgroundColor: colors.surface },
          ]}
          onPress={() => handleNavPress(item)}
        >
          <Ionicons name={item.icon} size={20} color={colors.text} />
          <Text style={[styles.navLabel, fontStyle, { color: colors.text }]}>
            {item.label}
          </Text>
        </Pressable>
      ))}
      {showDivider && <View style={[styles.divider, { backgroundColor: colors.border }]} />}
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background, borderRightColor: colors.border }]}>
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={{ paddingTop: Platform.OS !== 'web' ? insets.top : 16 }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      >
        {renderSection('', mainNavItems)}
        {renderSection('CUSTOM FEEDS', customFeedsItems)}
        {renderSection('RECENT', recentItems, false)}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 240,
    borderRightWidth: Platform.OS === 'web' ? 1 : 0,
    height: '100%',
  },
  scrollView: {
    flex: 1,
  },
  section: {
    paddingHorizontal: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
    marginBottom: 8,
    marginTop: 8,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 12,
  },
  navLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    marginVertical: 12,
  },
});
